import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "@/auth/session";
import Badge from "@/components/base/Badge";
import {
  createInstitutionalLoan,
  listInstitutionalLoans,
  updateInstitutionalLoanInstallment,
  updateInstitutionalLoanStatus,
  type CreateInstitutionalLoanPayload,
  type InstitutionalLoanItem,
  type UpdateLoanInstallmentPayload,
} from "@/lib/api";
import PrestamoDetalleModal from "@/pages/prestamos/components/PrestamoDetalleModal";
import NuevoPrestamoModal from "@/pages/prestamos/components/NuevoPrestamoModal";
import type { CuotaPrestamo, Prestamo } from "@/pages/prestamos/types";

const TIPO_LABEL: Record<string, string> = {
  bancario: "Bancario",
  fosis: "FOSIS",
  subdere: "SUBDERE",
  gore: "GORE",
  particular: "Particular",
};

const TIPO_COLOR: Record<string, string> = {
  bancario: "bg-indigo-100 text-indigo-700",
  fosis: "bg-emerald-100 text-emerald-700",
  subdere: "bg-teal-100 text-teal-700",
  gore: "bg-violet-100 text-violet-700",
  particular: "bg-amber-100 text-amber-700",
};

const ESTADO_VARIANT: Record<string, "success" | "warning" | "error" | "default"> = {
  vigente: "success",
  pagado: "default",
  mora: "error",
  refinanciado: "warning",
};

const ESTADO_LABEL: Record<string, string> = {
  vigente: "Vigente",
  pagado: "Pagado",
  mora: "En mora",
  refinanciado: "Refinanciado",
};

export default function PrestamosPage() {
  const navigate = useNavigate();
  const { session } = useSession();

  const [prestamos, setPrestamos] = useState<Prestamo[]>([]);
  const [selected, setSelected] = useState<Prestamo | null>(null);
  const [showNuevo, setShowNuevo] = useState(false);
  const [filterEstado, setFilterEstado] = useState("todos");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const formatCLP = (v: number) =>
    v.toLocaleString("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 });

  async function loadLoans(): Promise<void> {
    if (!session) return;
    setLoading(true);
    setError("");
    try {
      const rows = await listInstitutionalLoans(session);
      setPrestamos(rows.map(mapLoanToView));
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudieron cargar préstamos");
      setPrestamos([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadLoans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.accessToken]);

  const filtered = useMemo(
    () => prestamos.filter((p) => (filterEstado === "todos" ? true : p.estado === filterEstado)),
    [filterEstado, prestamos],
  );

  const totalDeuda = useMemo(
    () =>
      prestamos
        .filter((p) => p.estado === "vigente" || p.estado === "mora")
        .reduce((acc, p) => acc + (p.montoTotal - p.montoPagado), 0),
    [prestamos],
  );

  const totalPagado = useMemo(() => prestamos.reduce((acc, p) => acc + p.montoPagado, 0), [prestamos]);
  const vigentes = useMemo(() => prestamos.filter((p) => p.estado === "vigente").length, [prestamos]);

  async function handleCreateLoan(payload: CreateInstitutionalLoanPayload): Promise<void> {
    if (!session) return;
    await createInstitutionalLoan(session, payload);
    await loadLoans();
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/administracion")}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-lg" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Préstamos</h1>
            <p className="text-sm text-gray-500 mt-0.5">Registro y seguimiento de créditos institucionales</p>
          </div>
        </div>
        <button
          onClick={() => setShowNuevo(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line" /></div>
          Nuevo Préstamo
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-teal-50 flex-shrink-0">
            <i className="ri-bank-line text-lg text-teal-600" />
          </div>
          <div>
            <div className="text-xl font-bold text-gray-800">{vigentes}</div>
            <div className="text-xs text-gray-500">Préstamos vigentes</div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-red-100 p-4 flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-50 flex-shrink-0">
            <i className="ri-money-dollar-circle-line text-lg text-red-500" />
          </div>
          <div>
            <div className="text-xl font-bold text-red-600">{formatCLP(totalDeuda)}</div>
            <div className="text-xs text-gray-500">Saldo total por pagar</div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-50 flex-shrink-0">
            <i className="ri-checkbox-circle-line text-lg text-emerald-600" />
          </div>
          <div>
            <div className="text-xl font-bold text-emerald-600">{formatCLP(totalPagado)}</div>
            <div className="text-xs text-gray-500">Total pagado histórico</div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {["todos", "vigente", "mora", "pagado", "refinanciado"].map((f) => (
          <button
            key={f}
            onClick={() => setFilterEstado(f)}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap capitalize ${
              filterEstado === f
                ? "bg-teal-600 text-white"
                : "bg-white border border-gray-200 text-gray-600 hover:border-gray-300"
            }`}
          >
            {f === "todos" ? "Todos" : ESTADO_LABEL[f]}
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-3">
          {error}
        </div>
      )}

      <div className="space-y-3">
        {loading && (
          <div className="bg-white rounded-xl border border-gray-100 p-10 text-center text-sm text-gray-500">
            Cargando préstamos...
          </div>
        )}
        {!loading && filtered.map((p) => {
          const saldo = p.montoTotal - p.montoPagado;
          const pct = p.montoTotal > 0 ? Math.round((p.montoPagado / p.montoTotal) * 100) : 0;
          const cuotasPendientes = p.cuotasTotales - p.cuotasPagadas;

          return (
            <div
              key={p.id}
              onClick={() => setSelected(p)}
              className="bg-white rounded-xl border border-gray-100 p-5 hover:border-teal-200 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer group"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-gray-50 flex-shrink-0">
                    <i className="ri-bank-line text-xl text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-sm font-bold text-gray-800">{p.entidad}</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${TIPO_COLOR[p.tipo]}`}>
                        {TIPO_LABEL[p.tipo]}
                      </span>
                      <Badge variant={ESTADO_VARIANT[p.estado]} size="sm">{ESTADO_LABEL[p.estado]}</Badge>
                    </div>
                    <p className="text-xs text-gray-500 mb-3 line-clamp-1">{p.proposito}</p>

                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-xs text-gray-400">
                        <span>{p.cuotasPagadas} de {p.cuotasTotales} cuotas pagadas</span>
                        <span className="font-semibold text-teal-600">{pct}%</span>
                      </div>
                      <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${p.estado === "pagado" ? "bg-emerald-500" : p.estado === "mora" ? "bg-red-500" : "bg-teal-500"}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  <div className="text-right">
                    <div className="text-base font-bold text-gray-800">{formatCLP(p.montoTotal)}</div>
                    <div className="text-xs text-gray-400">Monto total</div>
                  </div>
                  {saldo > 0 && (
                    <div className="text-right">
                      <div className={`text-sm font-semibold ${p.estado === "mora" ? "text-red-500" : "text-gray-600"}`}>
                        {formatCLP(saldo)}
                      </div>
                      <div className="text-xs text-gray-400">Saldo pendiente</div>
                    </div>
                  )}
                  {cuotasPendientes > 0 && (
                    <div className="text-xs text-gray-400 bg-gray-50 px-2 py-1 rounded-lg">
                      {cuotasPendientes} cuota{cuotasPendientes !== 1 ? "s" : ""} pendiente{cuotasPendientes !== 1 ? "s" : ""}
                    </div>
                  )}
                  <i className="ri-arrow-right-line text-gray-300 group-hover:text-teal-400 transition-colors" />
                </div>
              </div>

              <div className="mt-3 pt-3 border-t border-gray-50 flex items-center gap-4 text-xs text-gray-400">
                <span><i className="ri-calendar-line mr-1" />{p.fechaInicio} → {p.fechaTermino}</span>
                <span><i className="ri-percent-line mr-1" />{p.tasaInteres}% anual</span>
                <span><i className="ri-money-dollar-circle-line mr-1" />{formatCLP(p.cuotasMensual)}/mes</span>
                <span className="font-mono text-gray-300">{p.numero}</span>
              </div>
            </div>
          );
        })}

        {!loading && filtered.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 mx-auto mb-3">
              <i className="ri-bank-line text-xl text-gray-400" />
            </div>
            <p className="text-sm text-gray-500">No hay préstamos con este estado</p>
          </div>
        )}
      </div>

      {selected && (
        <PrestamoDetalleModal
          prestamo={selected}
          onClose={() => setSelected(null)}
          onSetStatus={async (status) => {
            if (!session || !selected) return;
            const updated = await updateInstitutionalLoanStatus(session, selected.id, { status });
            setPrestamos((prev) => prev.map((item) => (item.id === selected.id ? mapLoanToView(updated) : item)));
            setSelected(mapLoanToView(updated));
          }}
          onUpdateCuota={async (cuotaId, payload) => {
            if (!session || !selected) return;
            const request: UpdateLoanInstallmentPayload = {
              status: payload.status,
              paidAt: payload.paidAt ?? null,
              reference: payload.reference ?? null,
            };
            const updated = await updateInstitutionalLoanInstallment(session, selected.id, cuotaId, request);
            setPrestamos((prev) => prev.map((item) => (item.id === selected.id ? mapLoanToView(updated) : item)));
            setSelected(mapLoanToView(updated));
          }}
        />
      )}

      {showNuevo && (
        <NuevoPrestamoModal
          onClose={() => {
            setShowNuevo(false);
          }}
          onConfirm={handleCreateLoan}
        />
      )}
    </div>
  );
}

function mapLoanToView(input: InstitutionalLoanItem): Prestamo {
  const cuotas: CuotaPrestamo[] = input.installments.map((installment) => ({
    id: installment.id,
    numero: installment.installmentNumber,
    fechaVencimiento: toDateOnly(installment.dueDate),
    monto: installment.amount,
    capital: installment.principalAmount,
    interes: installment.interestAmount,
    estado: installment.status,
    fechaPago: installment.paidAt ? toDateOnly(installment.paidAt) : undefined,
    referencia: installment.reference ?? undefined,
  }));

  return {
    id: input.id,
    numero: input.loanNumber,
    entidad: input.lenderName,
    tipo: input.loanType,
    proposito: input.purpose,
    montoTotal: input.totalAmount,
    montoPagado: input.paidAmount,
    cuotasMensual: input.monthlyInstallment,
    cuotasPagadas: input.installmentsPaid,
    cuotasTotales: input.installmentsTotal,
    tasaInteres: input.interestRate,
    fechaInicio: toDateOnly(input.startDate),
    fechaTermino: toDateOnly(input.endDate),
    estado: input.status,
    responsable: input.responsible,
    garantia: input.collateral ?? undefined,
    observaciones: input.notes ?? undefined,
    cuotas,
  };
}

function toDateOnly(value: string): string {
  if (!value) return "";
  return value.slice(0, 10);
}
